import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

import { canonicalizeClientCode } from '../session/client-code-canonicalizer';
import { isInternalRole } from '../session/session.model';
import { SessionService } from '../session/session.service';
import { FilterScopeController } from './filter-scope.service';

/**
 * The route path is a client-scope source (AITH-46 D-06).
 *
 * ## The defect this closes
 *
 * `FilterUrlProjection` reads the URL **once at initialisation** and only its query parameters — it
 * never reads the path. So arriving at `/clients/NB-0142` from AITH-45's top-clients drill-down
 * (`router.navigate(['/clients', code], { queryParamsHandling: 'preserve' })`, from a page where
 * the bar had no client selected) left `scope().clientCode === null`.
 *
 * `FilterOptionsService` keys its app, model and provider lists on that value, and AITH-44's R-003
 * renders all three as *"Select a client first"* when a staff caller has none. The result was three
 * permanently inert selectors on the one page where a client is **always** selected. The
 * 2026-08-26 comment on AITH-46 asked for this to be verified; it did not hold.
 *
 * ## Why it lives here and not in the page
 *
 * AITH-44 obligation 1 says consumers read `scope()` and never write it — and
 * {@link FilterScopeController} is root-provided, so nothing stops a page injecting it. Putting the
 * write in the filter feature keeps the feature the only writer, so the obligation survives as a
 * rule rather than becoming a convention the first page broke.
 *
 * ## What it does not do
 *
 * - **`FILTER_URL_PARAMS` is untouched.** A path-derived source changes no shared link's meaning.
 * - **Client roles are never affected.** Their scope is the session's identity-derived code, and
 *   FR-005 requires a URL-supplied client to have no effect for them. This applies to internal
 *   roles only.
 */
@Injectable()
export class ClientRouteScope {
  private readonly router = inject(Router);
  private readonly session = inject(SessionService);
  private readonly filters = inject(FilterScopeController);
  private readonly destroyRef = inject(DestroyRef);

  private started = false;

  initialize(): void {
    if (this.started) return;
    this.started = true;

    if (this.router.navigated) this.applyFrom(this.router.url);
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => this.applyFrom(event.urlAfterRedirects));
  }

  /**
   * Extracts a canonical client code from a `/clients/:clientCode` URL.
   *
   * Canonicalization order is load-bearing and delegated to
   * {@link canonicalizeClientCode}: trim, validate the **raw** value, then uppercase. Folding case
   * first would let `ſ001` (U+017F) uppercase onto `S001` and masquerade as another tenant's
   * canonical code. Exported for the spec — this is the part worth testing directly.
   */
  static clientCodeFrom(url: string): string | null {
    const path = url.split('?')[0]!.split('#')[0]!;
    const segments = path.split('/').filter((segment) => segment !== '');
    if (segments.length !== 2 || segments[0] !== 'clients') return null;

    // Malformed percent-encoding (`/clients/%E0%A4%A`) makes decodeURIComponent THROW a URIError.
    // This method's contract is `string | null` and its caller treats null as "no client in this
    // URL"; an exception is neither. RxJS 7 reports the throw as an unhandled error and keeps the
    // NavigationEnd subscription alive, so the observed damage was a console error plus one
    // silently skipped scope update -- but a segment that cannot be decoded is, definitionally,
    // not a canonicalizable client code, and that is what it should return.
    let decoded: string;
    try {
      decoded = decodeURIComponent(segments[1]!);
    } catch {
      return null;
    }
    return canonicalizeClientCode(decoded);
  }

  private applyFrom(url: string): void {
    // Internal roles only. A client role's scope is identity-derived and a URL value must never
    // reach it (FR-005).
    if (!isInternalRole(this.session.session())) return;

    const clientCode = ClientRouteScope.clientCodeFrom(url);
    if (clientCode === null) return;

    // Idempotent by construction. The setter would publish on every navigation otherwise, and the
    // page navigates when the published code changes — a pair that oscillates without this guard.
    if (this.filters.draftClientCode() === clientCode) return;

    this.filters.setClientCode(clientCode);
  }
}
